import { useState } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck } from "lucide-react";
import { useApi, useResource } from "../lib/api";
import { Loading, ErrorState } from "./Layout";

export function StepUpGate({ children }) {
  const api = useApi();
  const st = useResource("/stepup/status");
  const [enrol, setEnrol] = useState(null);
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState(null);
  const [busy, setBusy] = useState(false);
  if (st.loading) return <Loading label="Checking verification" />;
  if (st.error) return <ErrorState error={st.error} onRetry={st.reload} />;
  const s = st.data;
  if (!s.required || (s.verified_until && !backup)) return children;
  const start = async () => { try { setEnrol(await api("POST", "/stepup/enroll")); } catch (e) { toast.error(e.message); } };
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (s.enrolled) { await api("POST", "/stepup/verify", { code }); st.reload(); }
      else { const r = await api("POST", "/stepup/activate", { code }); setBackup(r.backup_codes); }
      setCode("");
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  if (backup) return (
    <div className="card p-6 max-w-lg space-y-4" data-testid="stepup-backup-codes">
      <h2 className="font-semibold">Save your backup codes</h2>
      <p className="text-sm muted">Each code works once if you lose your phone. They will not be shown again.</p>
      <div className="grid grid-cols-2 gap-2 font-mono text-sm">{backup.map((c) => <span key={c} className="card-2 p-2 text-center">{c}</span>)}</div>
      <button className="btn btn-primary" onClick={() => { setBackup(null); st.reload(); }} data-testid="stepup-backup-done">I have saved them</button>
    </div>);
  return (
    <div className="card p-6 max-w-lg space-y-4" data-testid="stepup-gate">
      <div className="flex items-center gap-2 font-semibold"><ShieldCheck className="text-[var(--mint)]" />Two-step verification required</div>
      <p className="text-sm muted">Administrators confirm a 6-digit code from an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…) once per session, valid for 8 hours. This is checked by the server on every administration request.</p>
      {!s.enrolled && !enrol && <button className="btn btn-primary" onClick={start} data-testid="stepup-start">Set up authenticator app</button>}
      {!s.enrolled && enrol && <div className="space-y-2"><div className="bg-white p-3 rounded-xl w-fit"><QRCodeSVG value={enrol.otpauth_uri} size={170} /></div>
        <p className="text-xs muted">Scan the QR code, or enter this key manually: <span className="font-mono break-all" data-testid="stepup-secret">{enrol.secret}</span></p></div>}
      {(s.enrolled || enrol) && <form onSubmit={submit} className="flex gap-2">
        <input className="input" inputMode="text" autoComplete="one-time-code" placeholder={s.enrolled ? "Code or backup code" : "6-digit code"} value={code} onChange={(e) => setCode(e.target.value)} data-testid="stepup-code" />
        <button className="btn btn-primary" disabled={busy || code.length < 6} data-testid="stepup-submit">{busy ? "…" : "Verify"}</button></form>}
    </div>);
}
